import {Component,OnDestroy,OnInit,ViewChild} from '@angular/core';
import {ActivatedRoute,Router} from "@angular/router";
import {filter,first} from "rxjs/operators";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {Session} from "@domain/security/session";
import {BehaviorSubject,Subject,Subscription} from "rxjs";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {isRouteAllowed} from "@core/security/role-admin-helpers";
import {Prestation} from "@domain/prestation/prestation";
import {PrestationService} from "@services/admin/frais/prestation.service";
import {FloatingButtonAction, TypeAction} from "@share/component/floating-button/floating-button";
import {FieldContainer} from "@domain/settings/field";
import {PrestationGeneralitesComponent} from "@components/admin/frais/prestations/prestation-detail/generalites/prestation-generalites.component";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {PrestationReglesGestionComponent} from "@components/admin/frais/prestations/prestation-detail/regles-gestion/prestation-regles-gestion.component";
import {PrestationZuComponent} from "@components/admin/frais/prestations/prestation-detail/zu/prestation-zu.component";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {TypeCodeErreur} from "@domain/common/http/result";
import {PrestationFiltresComponent} from "@components/admin/frais/prestations/prestation-detail/filtres/prestation-filtres.component";
import {PrestationDetailService} from "@components/admin/frais/prestations/prestation-detail/prestation-detail.service";

/**
 * Enum pour les noms des différents onglets de la page
 */
export enum Onglets {
	GENERALITES = "Generalites",
	REGLES = "Regles",
	ZU = "Zu",
	FILTRES = "Filtres"
}

/**
 * Composant de détail d'une prestation
 */
@Component({
	host: {'data-test-id': 'prestation-detail'},
	templateUrl: './prestation-detail.component.html',
	providers: [PrestationDetailService]
})
export class PrestationDetailComponent implements OnInit,OnDestroy {
	/** Onglets */
	Onglets = Onglets;

	/** ID de la prestation */
	idPrestation: string;

	/** Libellé de la prestation pour affichage dans le titre */
	libellePrestation: string;

	/** Liste statique des onglets */
	public static listeOnglets: Array<PageHeaderItem> = [
		{
			code: Onglets.GENERALITES,
			libelle: 'admin.frais.prestations.onglets.generalites',
			url: 'Admin/Frais/Prestation/:idPrestation/Generalites'
		},{
			code: Onglets.REGLES,
			libelle: 'admin.frais.prestations.onglets.reglesGestion',
			url: 'Admin/Frais/Prestation/:idPrestation/Regles'
		},{
			code: Onglets.ZU,
			libelle: 'admin.frais.prestations.onglets.zu',
			url: 'Admin/Frais/Prestation/:idPrestation/Zu'
		},{
			code: Onglets.FILTRES,
			libelle: 'admin.frais.prestations.onglets.filtres',
			url: 'Admin/Frais/Prestation/:idPrestation/Filtres'
		}
	];

	/** Liste des différents onglets disponibles */
	listeTabItems: Array<PageHeaderItem> = PrestationDetailComponent.listeOnglets;

	/** Index de l'onglet courant */
	selectedIndex: number;

	/** Onglet courant */
	selectedItem: PageHeaderItem;

	/** Liste des actions possibles */
	listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

	/** Session */
	private session: Session;

	/** Souscription */
	private subscriptions: Subscription[] = [];

	/** Prestation concernée */
	prestation : Prestation = new Prestation();

	/** Flag indiquant si la page est chargée */
	isLoaded: boolean = false;

	/** Wrapper pour les champs attribut */
	fieldContainer: FieldContainer;

	/** Évènement de rechargement des données de la page */
	reloadPageDataEvent: Subject<void> = new Subject<void>();

	/** Composant des généralités */
	@ViewChild('generalites') generalites: PrestationGeneralitesComponent;

	/** Composant des règles de gestion */
	@ViewChild('reglesGestion') reglesGestion: PrestationReglesGestionComponent;

	/** Composant des zu */
	@ViewChild('zu') zu: PrestationZuComponent;

	/** Composant des filtres */
	@ViewChild('filtres') filtres: PrestationFiltresComponent;

	/**
	 * Constructeur
	 */
	constructor(private store: Store<AppState>,
				private router: Router,
				private activatedRoute: ActivatedRoute,
				private prestationService: PrestationService,
				private translateService: TranslateService,
				private confirmService: ConfirmService,
				private toastrService: ToastrService) {}

	/**
	 * Initialisation du composant
	 */
	ngOnInit(): void {
		//Récupération de l'ID prestation dans la route
		this.activatedRoute.params.pipe(first()).subscribe(params => {
			//Sauvegarde de l'id de la prestation
			this.idPrestation = params['idPrestation'] as string;

			//Si un ID de prestation a bien été fourni
			if (!!this.idPrestation && this.idPrestation != "0") {
				//Chargement de la prestation
				this.load();
			} else {
				//Sinon, création d'une nouvelle prestation
				this.prestation = new Prestation();

				//Forçage à actif par défaut
				this.prestation.actif = true;

				//Synchronisation des champs attribut
				this.syncFieldContainer();

				//Objet chargé
				this.isLoaded = true;
			}

			//Récupération de la session
			this.store.select(state => state.session).pipe(filterFirstNotNull()).subscribe((session: Session) => {
				//Stockage de la session
				this.session = session;

				//Filtrage des onglets autorisés
				this.listeTabItems = !!this.idPrestation && this.idPrestation != "0" ? this.listeTabItems.filter(i => isRouteAllowed(this.router, this.session, i.url)) : [this.listeTabItems[0]];

				this.subscriptions.push(this.activatedRoute.url.subscribe(() => {
					//Récupération de la route
					const firstChild = this.activatedRoute.snapshot.firstChild;

					//Extraction du path
					const path = firstChild.url[0].path;

					//On vérifie si on trouve l'onglet demandé via l'url
					const matching = this.listeTabItems.find(i => i.code === path);

					//Si on trouve bien l'onglet correspondant
					if (!!matching) {
						//On met à jour le composant header
						setTimeout(() => {
							this.selectedItem = matching;
							this.selectedIndex = this.listeTabItems.findIndex(i => i.code === path);
						});
					} else {
						//Sinon, on redirige sur le premier onglet et on met à jour le composant header
						setTimeout(() => {
							this.selectedItem = this.listeTabItems[0];
							this.selectedIndex = 0;
						});
					}
				}));
			});
		});

		//Définition de la liste des actions du bouton en bas à droite de l'écran
		this.listeActions.next([{
			type: TypeAction.PRIMARY,
			icone: 'nio icon-sauvegarde',
			libelle: 'global.actions.enregistrer',
			doAction: () => this.save(),
		},{
			type: TypeAction.SECONDARY,
			icone: 'material-icons-outlined',
			iconeName: 'save_as',
			libelle: 'global.actions.dupliquer',
			isVisible: () => !!this.prestation?.idPrestation,
			doAction: () => this.duplicate(),
		},{
			type: TypeAction.SECONDARY,
			icone: 'nio icon-suppression',
			libelle: 'global.actions.supprimer',
			isVisible: () => !!this.prestation?.idPrestation,
			doAction: () => this.delete(),
		}]);
	}

	/**
	 * Chargement de la prestation
	 */
	private load(): void {
		//Chargement de la prestation
		this.prestationService.load(this.idPrestation).subscribe(prestation => {
			//Sauvegarde de la prestation
			this.prestation = prestation;

			//Affichage du libellé de la prestation
			this.libellePrestation = this.prestation.libelle;

			//Synchronisation des champs attribut
			this.syncFieldContainer();

			//Objet chargé
			this.isLoaded = true;
		});
	}

	/**
	 * Synchronisation des champs attribut
	 */
	private syncFieldContainer() {
		//Définition du fieldContainer pour la gestion des champs 'attribut'
		this.fieldContainer = new FieldContainer({});
		this.fieldContainer['myField5'] = this.prestation.attribut1;
		this.fieldContainer['myField6'] = this.prestation.attribut2;
		this.fieldContainer['myField7'] = this.prestation.attribut3;
		this.fieldContainer['myField8'] = this.prestation.attribut4;
	}

	/**
	 * Changement d'onglet
	 *
	 * @param selectedItem Onglet sélectionné
	 */
	onSelectedItemChange(selectedItem: PageHeaderItem): void {
		//Si aucun onglet n'est encore sélectionné
		if (!this.selectedItem) {
			//Application de l'onglet actif
			this.selectedItem = selectedItem;
		}

		//Si l'onglet actif est différent de l'onglet sélectionné
		if (selectedItem?.code !== this.selectedItem?.code) {
			//Application du nouvel onglet
			this.selectedItem = selectedItem;

			//Mise à jour de l'URL en conséquence
			this.router.navigateByUrl(selectedItem.url.replace(':idPrestation', this.idPrestation));
		}
	}

	/**
	 * Sauvegarde de la prestation
	 */
	save(): void {
		//Si les formulaires des différents onglets sont tous valides
		if (this.generalites?.form?.valid
				&& (this.reglesGestion?.form ? this.reglesGestion.form.valid : true)
				&& (this.zu?.form ? this.zu.form.valid : true)
				&& (this.filtres?.form ? this.filtres.form.valid : true)) {
			//Récupération des champs attribut
			this.prestation.attribut1 = this.fieldContainer['myField5'];
			this.prestation.attribut2 = this.fieldContainer['myField6'];
			this.prestation.attribut3 = this.fieldContainer['myField7'];
			this.prestation.attribut4 = this.fieldContainer['myField8'];

			//Sauvegarde de la prestation
			this.prestationService.save(this.prestation).subscribe(result => {
				//S'il n'y a pas d'erreur
				if (result.codeErreur == TypeCodeErreur.NO_ERROR) {
					//Mise à jour de l'id de la prestation
					this.idPrestation = result.data.idPrestation.toString();
					this.prestation.idPrestation = result.data.idPrestation as number;
					this.router.navigateByUrl(this.selectedItem.url.replace(':idPrestation', this.idPrestation));

					//Rechargement complet de l'objet
					this.load();

					//Rechargement des données de la page, car des choix peuvent avoir été ajoutés dans certains selects
					this.reloadPageDataEvent.next();

					//Mise à jour des onglets affichés
					this.listeTabItems = PrestationDetailComponent.listeOnglets.filter(i => isRouteAllowed(this.router, this.session, i.url));

					//Message de succès
					this.toastrService.success(this.translateService.instant('global.success.enregistrement'));
				} else if (result.codeErreur == TypeCodeErreur.ERROR_DOUBLON) {
					//Sinon, s'il y a une erreur de doublon, message d'erreur de code déjà existant
					this.toastrService.error(this.translateService.instant('admin.frais.prestations.detail.generalites.infosGenerales.erreurDoublonCode'));
				} else {
					//Sinon message d'erreur générique
					this.toastrService.error(this.translateService.instant('global.errors.enregistrement'));
				}
			});
		} else {
			//Sinon, on renvoie une erreur
			this.toastrService.error(this.translateService.instant('global.errors.formInvalid'));
		}
	}

	/**
	 * Suppression d'une prestation
	 */
	delete() {
		//Confirmation de la suppression
		this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
			.pipe(filter(isConfirmed => isConfirmed)).subscribe({
				next: () => {
					//Suppression
					this.prestationService.delete(this.idPrestation).subscribe(res => {
						if(res.codeErreur !== TypeCodeErreur.NO_ERROR) {
							//Si le résultat contient une erreur, alors on affiche un message d'erreur
							this.toastrService.error(this.translateService.instant('admin.frais.prestations.detail.erreurDelete'));
						} else {
							//Sinon on affiche un message de succès
							this.toastrService.success(this.translateService.instant('global.success.suppression'));

							//Et on redirige vers la liste
							this.router.navigate(['Admin/Frais/Prestations/Liste']);
						}
					});
				}
			}
		);
	}

	/**
	 * Duplication d'une prestation
	 */
	duplicate() {
		this.prestationService.duplicate(this.idPrestation).subscribe(res => {
			if(res.codeErreur !== TypeCodeErreur.NO_ERROR) {
				//Si le résultat contient une erreur, alors on affiche un message d'erreur
				this.toastrService.error(this.translateService.instant('global.errors.enregistrement'));
			} else {
				//Sinon on affiche un message de succès
				this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

				//Mise à jour de l'id de la prestation
				this.idPrestation = res.data.idPrestation;
				this.router.navigateByUrl(this.selectedItem.url.replace(':idPrestation', this.idPrestation));

				//Rechargement complet de l'objet
				this.load();

				//Rechargement des données de la page, car des choix peuvent avoir été ajoutés dans certains selects
				this.reloadPageDataEvent.next();
			}
		});
	}

	/**
	 * Retour à la liste
	 */
	onGoBack(): void {
		//Retour à la liste
		this.router.navigate(['Admin/Frais/Prestations/Liste']);
	}

	/**
	 * Destruction du composant
	 */
	ngOnDestroy(): void {
		//Résiliation des abonnements
		this.subscriptions.forEach(s => s.unsubscribe());
	}
}